/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { PDFDocument } from "pdf-lib";
import moment from "moment";
import { useSelector } from "react-redux";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import nationalities from "i18n-nationality";
import nationalitiesFr from "i18n-nationality/langs/fr.json";
import Teachr from "../Objects/models/Teachr";
import { isAeProcurationCode, isSapProcurationCode, resolveProcurationCode } from "../Helpers/ProcurationType";
import {
  FormDataFirstPart,
  FormDataSecondPart,
  FormDataThirdPart,
  FormDataFourthPart,
  FormDataFifthPart,
  FormErrorsFirstPart,
  FormErrorsSecondPart,
  FormErrorsThirdPart,
  FormErrorsFourthPart,
  FormErrorsFifthPart,
  FormDataFirstPartSap,
  FormErrorsFirstPartSap,
  FormDataSecondPartSap,
  FormErrorsSecondPartSap,
  changeInputData,
  changeInputInterface,
  LegalRepresentative,
  DialogFormData,
  DialogErrors,
  // DialogErrors,
} from "./InterfaceProcuration";

interface useProcurationProps {
  pdfDoc: PDFDocument | undefined;
  teachr: Teachr;
  triggerPdfUpdate?: () => void;
  setPage?: React.Dispatch<React.SetStateAction<number>>
}

countries.registerLocale(fr);
nationalities.registerLocale(nationalitiesFr as { locale: string; nationalities: Record<string, string> });

export const otherActivitiesList = [
  "Entretien de la maison et travaux ménagers",
  "Petits travaux de jardinage",
  "Travaux de petit bricolage",
  "Garde d'enfants de plus de 3 ans à domicile",
  "Soutien scolaire ou cours à domicile",
  "Soins d'esthétique à domicile pour les personnes dépendantes",
  "Préparation de repas à domicile",
  "Livraison de repas à domicile",
  "Livraison de courses à domicile",
  "Collecte et livraison à domicile de linge repassé",
  "Assistance informatique à domicile",
  "Soins et promenades d'animaux de compagnie pour les personnes dépendantes",
  "Maintenance, entretien et vigilance temporaires à domicile",
  "Assistance administrative à domicile",
  "Accompagnement des enfants de plus de 3 ans dans leurs déplacements",
  "Télé-assistance et visio-assistance",
  "Interprète en langue des signes",
  "Assistance aux personnes ayant besoin d'une aide temporaire à leur domicile",
  "Conduite du véhicule des personnes en cas d'invalidité temporaire",
  "Accompagnement des personnes présentant une invalidité temporaire",
  "Coordination et délivrance des services à la personne",
  "Garde d'enfants de moins de 3 ans et de moins de 18 ans handicapés à domicile",
  "Accompagnement d'enfants de moins de 3 ans et de moins de 18 ans handicapés",
  "Assistance aux personnes âgées et aux personnes handicapées",
  "Conduite du véhicule des personnes ayant des difficultés de mobilité",
  "Accompagnement des personnes en dehors de leur domicile",
];

export const listeCountries = countries.getNames("fr");
export const listeNationalities = Object.keys(listeCountries).map(
  (code) => nationalities.getName(code, "fr") || (listeCountries as Record<string, string>)[code]
);

// Accès "safe" aux champs du formulaire PDF : certains documents (ex. la
// variante declarons) ne contiennent pas tous les champs présents dans le
// document TeachR. pdf-lib lève une exception si le champ n'existe pas, on
// retourne donc null pour que le code en aval (déjà protégé par des null-checks)
// puisse remplir uniquement les champs réellement présents.
type AnyPdfForm = ReturnType<PDFDocument["getForm"]>;
const getTextFieldSafe = (form: AnyPdfForm, name: string) => {
  try {
    return form.getTextField(name);
  } catch {
    return null as any;
  }
};
const getDropdownSafe = (form: AnyPdfForm, name: string) => {
  try {
    return form.getDropdown(name);
  } catch {
    return null as any;
  }
};
const getRadioGroupSafe = (form: AnyPdfForm, name: string) => {
  try {
    return form.getRadioGroup(name);
  } catch {
    return null as any;
  }
};

export const useProcuration = ({
  pdfDoc,
  teachr,
  triggerPdfUpdate,
  setPage
}: useProcurationProps) => {
  const dataProcuration = useSelector((state: any) => state.procurationAe);
  const dataProcurationSap = useSelector((state: any) => state.procurationSap);
  const currentProcuration = useSelector((state: any) => state.user.currentProcuration)
  // Même résolution que dans IndexProcuration : le code porté par le compte
  // prime sur la valeur Redux. Les gardes ci-dessous acceptent les codes
  // actuels (30 = AE, 20 = SAP) et les anciens (1 = AE, 2 = SAP) : ne
  // reconnaître que 1 et 2 laissait le PDF vide pour tout client arrivant
  // avec le code 30.
  const procurationCode = resolveProcurationCode(teachr?.procuration, currentProcuration)
  const isAeProcuration = isAeProcurationCode(procurationCode)
  const isSapProcuration = isSapProcurationCode(procurationCode)
  const firstPart: FormDataFirstPart = dataProcuration?.secondProc || {};
  const firstPartSap: FormDataFirstPartSap = dataProcurationSap?.first || {};
  const secondPart: FormDataSecondPart = dataProcuration?.fourthProc || {};
  const thirdPart: FormDataThirdPart = dataProcuration?.fifthProc || {};
  const fourthPart: FormDataFourthPart = dataProcuration?.sixthProc || {};
  const fifthPart: FormDataFifthPart = dataProcuration?.seventhProc || {};
  const legalRepProc: LegalRepresentative[] = dataProcuration?.legalRepProc || [];
  const secondPartSap: FormDataSecondPartSap = dataProcurationSap?.second || {};

  const pdfFiledFieldsFirst = useMemo(() => {
    if (!pdfDoc) {
      // Retourner des objets vides si pdfDoc n'est pas disponible
      return {
        firstNameField1: null as any,
        lastNameField1: null as any,
        firstNameField2: null as any,
        lastNameField2: null as any,
        birthDateField1: null as any,
        birthDateField2: null as any,
        birthCityField1: null as any,
        birthCityField2: null as any,
        addressField1: null as any,
        addressField2: null as any,
        signCityField: null as any,
        genderField: null as any,
        phoneField: null as any,
        countryField: null as any,
        nationalityField: null as any,
        maritalStatusField: null as any,
      };
    }
    if (!isAeProcuration) {
      return {
        firstNameField1: null as any,
        lastNameField1: null as any,
        firstNameField2: null as any,
        lastNameField2: null as any,
        birthDateField1: null as any,
        birthDateField2: null as any,
        birthCityField1: null as any,
        birthCityField2: null as any,
        addressField1: null as any,
        addressField2: null as any,
        signCityField: null as any,
        genderField: null as any,
        phoneField: null as any,
        countryField: null as any,
        nationalityField: null as any,
        maritalStatusField: null as any,
      };
    }
    const form = pdfDoc.getForm();
    return {
      firstNameField1: getTextFieldSafe(form, "text_2szbk"),
      lastNameField1: getTextFieldSafe(form, "text_3ggbf"),
      firstNameField2: getTextFieldSafe(form, "text_9khzw"),
      lastNameField2: getTextFieldSafe(form, "text_10zhxm"),
      birthDateField1: getTextFieldSafe(form, "text_4uena"),
      birthDateField2: getTextFieldSafe(form, "text_13mwhy"),
      birthCityField1: getTextFieldSafe(form, "text_5pkzj"),
      birthCityField2: getTextFieldSafe(form, "text_15zpyt"),
      addressField1: getTextFieldSafe(form, "text_6zwby"),
      addressField2: getTextFieldSafe(form, "text_47frij"),
      signCityField: getTextFieldSafe(form, "text_7kchz"),
      genderField: getDropdownSafe(form, "dropdown_12tbys"),
      phoneField: getTextFieldSafe(form, "text_1htbt"),
      countryField: getTextFieldSafe(form, "text_14gqys"),
      nationalityField: getTextFieldSafe(form, "text_2lkfd"),
      maritalStatusField: getRadioGroupSafe(form, "radio_group_37rzdc"),
    };
  }, [pdfDoc, isAeProcuration]);

  const pdfFiledFieldsFirstSap = useMemo(() => {
    if (!pdfDoc) {
      return {
        fullNameField: null as any,
        birthDateField: null as any,
        cityBirthField: null as any,
        siretField: null as any,
        addressField: null as any,
        signCityField: null as any,
      };
    }

    if (!isSapProcuration) {
      return {
        fullNameField: null as any,
        birthDateField: null as any,
        cityBirthField: null as any,
        siretField: null as any,
        addressField: null as any,
        signCityField: null as any,
      };
    }

    const form = pdfDoc.getForm();
    return {
      fullNameField: getTextFieldSafe(form, 'text_1rgez'),
      birthDateField: getTextFieldSafe(form, 'text_2azmj'),
      cityBirthField: getTextFieldSafe(form, 'text_3bvew'),
      siretField: getTextFieldSafe(form, 'text_4kpir'),
      addressField: getTextFieldSafe(form, 'text_16unmp'),
      signCityField: getTextFieldSafe(form, 'text_5nlom'),
    };
  }, [pdfDoc, isSapProcuration]);

  const pdfFiledFieldsSecondSap = useMemo(() => {
    if (!pdfDoc) {
      return {
        codeApeField: null as any,
        createdAtField: null as any,
        enterprisePhoneField: null as any,
        enterpriseEmailField: null as any,
        chiefFirstNameField: null as any,
        chiefLastNameField: null as any,
        chiefPhoneField: null as any,
        genderField: null as any,
      };
    }

    if (!isSapProcuration) {
      return {
        codeApeField: null as any,
        createdAtField: null as any,
        enterprisePhoneField: null as any,
        enterpriseEmailField: null as any,
        chiefFirstNameField: null as any,
        chiefLastNameField: null as any,
        chiefPhoneField: null as any,
        genderField: null as any,
      };
    }

    const form = pdfDoc.getForm();
    return {
      codeApeField: getTextFieldSafe(form, 'text_7cfjc'),
      createdAtField: getTextFieldSafe(form, 'text_8fxmo'),
      enterprisePhoneField: getTextFieldSafe(form, 'text_9psux'),
      enterpriseEmailField: getTextFieldSafe(form, 'text_10xfe'),
      chiefFirstNameField: getTextFieldSafe(form, 'text_12nvnu'),
      chiefLastNameField: getTextFieldSafe(form, 'text_11jqek'),
      chiefPhoneField: getTextFieldSafe(form, 'text_13jxjc'),
      genderField: getRadioGroupSafe(form, 'radio_group_1ghva'),
    };
  }, [pdfDoc, isSapProcuration]);

  const pdfFiledFieldsSecond = useMemo(() => {
    if (!pdfDoc) {
      // Retourner des objets vides si pdfDoc n'est pas disponible
      return {
        socialSecurityNumberField: null as any,
        socialOrganismField: null as any,
        socialTaxField: null as any,
        healthInsuranceOtherField: null as any,
        isActivityField: null as any,
        activityNameField: null as any,
        isAeField: null as any,
      };
    }

    if (!isAeProcuration) {
      return {
        socialSecurityNumberField: null as any,
        socialOrganismField: null as any,
        socialTaxField: null as any,
        healthInsuranceOtherField: null as any,
        isActivityField: null as any,
        activityNameField: null as any,
        isAeField: null as any,
      };
    }

    const form = pdfDoc.getForm();
    return {
      socialSecurityNumberField: getTextFieldSafe(form, "text_34jxhh"),
      socialOrganismField: getRadioGroupSafe(form, "radio_group_45munl"),
      socialTaxField: getRadioGroupSafe(form, "radio_group_1fgsy"),
      healthInsuranceOtherField: getTextFieldSafe(form, "text_1nung"),
      isActivityField: getRadioGroupSafe(form, "radio_group_68drcz"),
      activityNameField: getTextFieldSafe(form, "text_66kbwq"),
      isAeField: getRadioGroupSafe(form, "radio_group_51dxxa"),
    };
  }, [pdfDoc, isAeProcuration]);

  const pdfFiledFieldsThird = useMemo(() => {
    if (!pdfDoc) {
      // Retourner des objets vides si pdfDoc n'est pas disponible
      return {
        otherActivityField: null as any,
        activitiesOptionsField: null as any,
        fixAddressAtHome: null as any,
        useDomiciliationCompany: null as any,
        optForLiberatoryPaymentField: null as any,
      };
    }

    if (!isAeProcuration) {
      return {
        otherActivityField: null as any,
        activitiesOptionsField: null as any,
        fixAddressAtHome: null as any,
        useDomiciliationCompany: null as any,
        optForLiberatoryPaymentField: null as any,
      };
    }

    const form = pdfDoc.getForm();
    return {
      otherActivityField: getRadioGroupSafe(form, "radio_group_53iyyl"),
      activitiesOptionsField: getTextFieldSafe(form, "text_44bwe"),
      fixAddressAtHome: getRadioGroupSafe(form, "radio_group_55qzhv"),
      useDomiciliationCompany: getRadioGroupSafe(form, "radio_group_57wzmj"),
      optForLiberatoryPaymentField: getRadioGroupSafe(form, "radio_group_67ihup"),

    };
  }, [pdfDoc, isAeProcuration]);

  const pdfFiledFieldsFourth = useMemo(() => {
    if (!pdfDoc) {
      // Retourner des objets vides si pdfDoc n'est pas disponible
      return {
        renounceInsaisissabilityField: null as any,
        addCommercialNameField: null as any,
        employEmployeesField: null as any,
        employeeCountField: null as any,

        addOtherLegalRepresentatives: null as any,
        otherChiefFirstNameField: null as any,
        otherChiefNameField: null as any,
        otherChiefNationalField: null as any,
        otherChiefbirthCountryField: null as any,
        otherChiefgenderField: null as any,
      };
    }

    if (!isAeProcuration) {
      return {
        renounceInsaisissabilityField: null as any,
        addCommercialNameField: null as any,
        employEmployeesField: null as any,
        employeeCountField: null as any,
        addOtherLegalRepresentatives: null as any,
        otherChiefFirstNameField: null as any,
        otherChiefNameField: null as any,
        otherChiefNationalField: null as any,
        otherChiefbirthCountryField: null as any,
        otherChiefgenderField: null as any,
      };
    }

    const form = pdfDoc.getForm();
    return {
      renounceInsaisissabilityField: getRadioGroupSafe(form, "radio_group_59psyj"),
      addCommercialNameField: getRadioGroupSafe(form, "radio_group_61dyou"),
      employEmployeesField: getRadioGroupSafe(form, "radio_group_63tvft"),
      employeeCountField: getTextFieldSafe(form, "text_50qete"),


      addOtherLegalRepresentatives: getRadioGroupSafe(form, "radio_group_73axhk"),
      otherChiefFirstNameField: getTextFieldSafe(form, "text_71bthn"),
      otherChiefNameField: getTextFieldSafe(form, "text_72flsj"),
      otherChiefNationalField: getTextFieldSafe(form, "text_75ofjg"),
      otherChiefbirthCountryField: getTextFieldSafe(form, "text_76knwj"),
      otherChiefgenderField: getDropdownSafe(form, "dropdown_74dqgu"),
    };
  }, [pdfDoc, isAeProcuration]);

  const pdfFiledFieldsFifth = useMemo(() => {
    if (!pdfDoc) {
      // Retourner des objets vides si pdfDoc n'est pas disponible
      return {
        authorizeSirenDirectoryAccess: null as any,
        authorizePersonalDataProspecting: null as any,
        addDomainNameField: null as any,
        domainNamesecondSaveField: null as any,
      };
    }
    if (!isAeProcuration) {
      return {
        authorizeSirenDirectoryAccess: null as any,
        authorizePersonalDataProspecting: null as any,
        addDomainNameField: null as any,
        domainNamesecondSaveField: null as any,
      };
    }

    const form = pdfDoc.getForm();
    return {
      authorizeSirenDirectoryAccess: getRadioGroupSafe(form, "radio_group_69lcym"),
      authorizePersonalDataProspecting: getRadioGroupSafe(form, "radio_group_71kfss"),
      addDomainNameField: getRadioGroupSafe(form, "radio_group_65cldx"),
      domainNamesecondSaveField: getTextFieldSafe(form, "text_49atxb"),
    };
  }, [pdfDoc, isAeProcuration]);

  const setGenderField = useCallback((value: number) => {
    if (!pdfFiledFieldsFirst?.genderField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFirst?.genderField.select("Féminin");
        break;
      case 2:
        pdfFiledFieldsFirst?.genderField.select("Masculin");
        break;
      default:
        pdfFiledFieldsFirst?.genderField.select("Féminin");
        break;
    }
  }, [pdfFiledFieldsFirst?.genderField])
  const setNationality = useCallback((value: number) => {
    if (!pdfFiledFieldsFirst.nationalityField) return;
    // `value` est l'index (base 1) choisi dans la liste des nationalités
    // affichée par le formulaire : on reporte le même libellé dans le PDF.
    // Le champ doit rester dans les dépendances : le document est rechargé à
    // chaque régénération de l'aperçu, un tableau vide figeait une référence
    // vers un document abandonné et la nationalité n'était jamais écrite.
    const nationality = listeNationalities[value - 1] ?? '';
    pdfFiledFieldsFirst.nationalityField.setText(nationality);
  }, [pdfFiledFieldsFirst.nationalityField])
  const setCountryField = useCallback((value: number) => {
    const countryNames = Object.values(listeCountries);
    const countryName = countryNames[value - 1] || '';
    if (pdfFiledFieldsFirst.countryField) {
      pdfFiledFieldsFirst.countryField.setText(countryName);
    }
  }, [listeCountries, pdfFiledFieldsFirst.countryField])
  const setMaritalStatusField = useCallback((value: number) => {
    if (!pdfFiledFieldsFirst.maritalStatusField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFirst.maritalStatusField.select("Value_zpyl"); //ok
        break;
      case 2:
        pdfFiledFieldsFirst.maritalStatusField.select("Value_gati"); //ok
        break;
      case 3:
        pdfFiledFieldsFirst.maritalStatusField.select("Value_stfi");//ok
        break;
      case 4:
        pdfFiledFieldsFirst.maritalStatusField.select("Value_vvki");
        break;
      case 5:
        pdfFiledFieldsFirst.maritalStatusField.select("Value_qtvb");
        break;
      case 6:
        pdfFiledFieldsFirst.maritalStatusField.select("Value_qatz"); // ok
        break;
      default:
        pdfFiledFieldsFirst.maritalStatusField.select("Value_zpyl");
        break;
    }
  }, [pdfFiledFieldsFirst.maritalStatusField])

  const setHealthInsuranceField = useCallback((value: number) => {
    if (!pdfFiledFieldsSecond.socialOrganismField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsSecond.socialOrganismField.select("Value_elbp");
        break;
      case 2:
        pdfFiledFieldsSecond.socialOrganismField.select("Value_ccea");
        break;
      case 3:
        pdfFiledFieldsSecond.socialOrganismField.select("Value_uzwh");
        break;
      case 4:
        pdfFiledFieldsSecond.socialOrganismField.select("Value_qzzc");
        break;
      case 5:
        pdfFiledFieldsSecond.socialOrganismField.select("Value_hplx");
        break;
      case 6:
        pdfFiledFieldsSecond.socialOrganismField.select("Value_mdl");
        break;
      default:
        pdfFiledFieldsSecond.socialOrganismField.select("Value_elbp");
        break;
    }
  }, [pdfFiledFieldsSecond.socialOrganismField])

  const setSocialTaxField = useCallback((value: number) => {
    if (!pdfFiledFieldsSecond.socialTaxField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsSecond.socialTaxField.select("Value_ruy");
        break;
      case 2:
        pdfFiledFieldsSecond.socialTaxField.select("Value_yibd");
        break;
      default:
        pdfFiledFieldsSecond.socialTaxField.select("Value_ruy");
        break;
    }
  }, [pdfFiledFieldsSecond.socialTaxField])

  const setIsActivityField = useCallback((value: number) => {
    if (!pdfFiledFieldsSecond.isActivityField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsSecond.isActivityField.select("Value_adqg");
        break;
      case 2:
        pdfFiledFieldsSecond.activityNameField.setText("")
        pdfFiledFieldsSecond.isActivityField.select("Value_vvog");
        break;
      default:
        pdfFiledFieldsSecond.isActivityField.select("Value_vvog");
        break;
    }
  }, [pdfFiledFieldsSecond.activityNameField, pdfFiledFieldsSecond.isActivityField]);

  const setIsAeField = useCallback((value: number) => {
    if (!pdfFiledFieldsSecond.isAeField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsSecond.isAeField.select("Value_dfii");
        break;
      case 2:
        pdfFiledFieldsSecond.isAeField.select("Value_qmgq");
        break;
      default:
        pdfFiledFieldsSecond.isAeField.select("Value_qmgq");
        break;
    }
  }, [pdfFiledFieldsSecond.isAeField]);

  const setOtherActivityField = useCallback((value: number) => {
    if (!pdfFiledFieldsThird.otherActivityField) return;

    switch (value) {
      case 1:
        pdfFiledFieldsThird.otherActivityField.select("Value_zzea");
        break;
      case 2:
        if (pdfFiledFieldsThird.activitiesOptionsField) {
          pdfFiledFieldsThird.activitiesOptionsField.setText("");
        }
        pdfFiledFieldsThird.otherActivityField.select("Value_ul");
        break;
      default:
        pdfFiledFieldsThird.otherActivityField.select("Value_ul");
        break;
    }
  }, [pdfFiledFieldsThird.otherActivityField, pdfFiledFieldsThird.activitiesOptionsField]);

  const setUseDomiciliationCompanyField = useCallback((value: number) => {
    if (!pdfFiledFieldsThird.useDomiciliationCompany) return;
    switch (value) {
      case 1:
        pdfFiledFieldsThird.useDomiciliationCompany.select("Value_zpxw");
        break;
      case 2:
        pdfFiledFieldsThird.useDomiciliationCompany.select("Value_giai");
        break;
      default:
        pdfFiledFieldsThird.useDomiciliationCompany.select("Value_giai");
        break;
    }
  }, [pdfFiledFieldsThird.useDomiciliationCompany]);

  const setFixAddressAtHomeField = useCallback((value: number) => {
    if (!pdfFiledFieldsThird.fixAddressAtHome) return;
    switch (value) {
      case 1:
        pdfFiledFieldsThird.fixAddressAtHome.select("Value_plmt");
        // Si l'adresse est fixée au domicile, réinitialiser useDomiciliationCompany
        if (pdfFiledFieldsThird.useDomiciliationCompany) {
          pdfFiledFieldsThird.useDomiciliationCompany.select("Value_giai");
        }
        break;
      case 2:
        pdfFiledFieldsThird.fixAddressAtHome.select("Value_rtku");
        break;
      default:
        pdfFiledFieldsThird.fixAddressAtHome.select("Value_rtku");
        break;
    }
  }, [pdfFiledFieldsThird.fixAddressAtHome, pdfFiledFieldsThird.useDomiciliationCompany]);


  const setAddOtherLegalRepresentativesField = useCallback((value: number) => {
    if (!pdfFiledFieldsFourth?.addOtherLegalRepresentatives) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFourth.addOtherLegalRepresentatives.select("Value_dbb");
        break;
      case 2:
        pdfFiledFieldsFourth.addOtherLegalRepresentatives.select("Value_ausx");
        // Réinitialiser les champs du représentant légal si "Non" est sélectionné
        if (pdfFiledFieldsFourth.otherChiefFirstNameField) {
          pdfFiledFieldsFourth.otherChiefFirstNameField.setText("");
        }
        if (pdfFiledFieldsFourth.otherChiefNameField) {
          pdfFiledFieldsFourth.otherChiefNameField.setText("");
        }
        if (pdfFiledFieldsFourth.otherChiefNationalField) {
          pdfFiledFieldsFourth.otherChiefNationalField.setText("");
        }
        if (pdfFiledFieldsFourth.otherChiefbirthCountryField) {
          pdfFiledFieldsFourth.otherChiefbirthCountryField.setText("");
        }
        if (pdfFiledFieldsFourth.otherChiefgenderField) {
          pdfFiledFieldsFourth.otherChiefgenderField.select("Féminin");
        }
        break;
      default:
        pdfFiledFieldsFourth.addOtherLegalRepresentatives.select("Value_dbb");
        break;
    }
  }, [pdfFiledFieldsFourth]);

  const setOtherChiefGenderField = useCallback((value: number) => {
    if (!pdfFiledFieldsFourth.otherChiefgenderField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFourth.otherChiefgenderField.select("Féminin");
        break;
      case 2:
        pdfFiledFieldsFourth.otherChiefgenderField.select("Masculin");
        break;
      default:
        pdfFiledFieldsFourth.otherChiefgenderField.select("Féminin");
        break;
    }
  }, [pdfFiledFieldsFourth?.otherChiefgenderField]);
  const setOtherChiefNationalField = useCallback((value: number) => {
    if (!pdfFiledFieldsFourth.otherChiefNationalField) return;
    if (value === 1) {
      pdfFiledFieldsFourth.otherChiefNationalField.setText("Française");
    } else {
      pdfFiledFieldsFourth.otherChiefNationalField.setText("Étrangère");
    }
  }, [pdfFiledFieldsFourth.otherChiefNationalField]);

  const setRenounceInsaisissabilityField = useCallback((value: number) => {
    if (!pdfFiledFieldsFourth?.renounceInsaisissabilityField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFourth.renounceInsaisissabilityField.select("Value_ypo");
        break;
      case 2:
        pdfFiledFieldsFourth.renounceInsaisissabilityField.select("Value_ntgw");
        break;
      default:
        pdfFiledFieldsFourth.renounceInsaisissabilityField.select("Value_ntgw");
        break;
    }
  }, [pdfFiledFieldsFourth.renounceInsaisissabilityField]);

  const setAddCommercialNameField = useCallback((value: number) => {
    if (!pdfFiledFieldsFourth.addCommercialNameField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFourth.addCommercialNameField.select("Value_gryw");
        break;
      case 2:
        pdfFiledFieldsFourth.addCommercialNameField.select("Value_sfkw");
        break;
      default:
        pdfFiledFieldsFourth.addCommercialNameField.select("Value_sfkw");
        break;
    }
  }, [pdfFiledFieldsFourth.addCommercialNameField]);

  const setEmployEmployeesField = useCallback((value: number) => {
    if (!pdfFiledFieldsFourth.employEmployeesField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFourth.employEmployeesField.select("Value_qpgv");
        break;
      case 2:
        pdfFiledFieldsFourth.employEmployeesField.select("Value_bnsk");
        break;
      default:
        pdfFiledFieldsFourth.employEmployeesField.select("Value_bnsk");
        break;
    }
  }, [pdfFiledFieldsFourth.employEmployeesField]);

  const setOptForLiberatoryPaymentField = useCallback((value: number) => {
    if (!pdfFiledFieldsThird.optForLiberatoryPaymentField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsThird.optForLiberatoryPaymentField.select("Value_qaka");
        break;
      case 2:
        pdfFiledFieldsThird.optForLiberatoryPaymentField.select("Value_byhs");
        break;
      default:
        pdfFiledFieldsThird.optForLiberatoryPaymentField.select("Value_byhs");
        break;
    }
  }, [pdfFiledFieldsThird.optForLiberatoryPaymentField]);

  const setAuthorizeSirenDirectoryAccessField = useCallback((value: number) => {
    if (!pdfFiledFieldsFifth.authorizeSirenDirectoryAccess) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFifth.authorizeSirenDirectoryAccess.select("Value_lpyp");
        break;
      case 2:
        pdfFiledFieldsFifth.authorizeSirenDirectoryAccess.select("Value_boeh");
        break;
      default:
        pdfFiledFieldsFifth.authorizeSirenDirectoryAccess.select("Value_boeh");
        break;
    }
  }, [pdfFiledFieldsFifth.authorizeSirenDirectoryAccess]);

  const setAuthorizePersonalDataProspectingField = useCallback((value: number) => {
    if (!pdfFiledFieldsFifth.authorizePersonalDataProspecting) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFifth.authorizePersonalDataProspecting.select(
          "Value_ltqx",
        );
        break;
      case 2:
        pdfFiledFieldsFifth.authorizePersonalDataProspecting.select(
          "Value_zbzj",
        );
        break;
      default:
        pdfFiledFieldsFifth.authorizePersonalDataProspecting.select(
          "Value_zbzj",
        );
        break;
    }
  }, [pdfFiledFieldsFifth.authorizePersonalDataProspecting]);

  const setAddDomainNameField = useCallback((value: number) => {
    if (!pdfFiledFieldsFifth.addDomainNameField) return;
    switch (value) {
      case 1:
        pdfFiledFieldsFifth.addDomainNameField.select("Value_boxp");
        break;
      case 2:
        pdfFiledFieldsFifth.domainNamesecondSaveField.setText('');
        pdfFiledFieldsFifth.addDomainNameField.select("Value_lwgq");
        break;
      default:
        pdfFiledFieldsFifth.addDomainNameField.select("Value_lwgq");
        break;
    }
  }, [pdfFiledFieldsFifth.addDomainNameField, pdfFiledFieldsFifth.domainNamesecondSaveField]);

  const setActivitiesOptionsField = useCallback((values: number[]) => {
    if (!pdfFiledFieldsThird.activitiesOptionsField) return;
    const activitiesList = otherActivitiesList;
    // Convertir le tableau d'indices en texte séparé par des virgules
    const selectedActivities = values
      .filter(value => value > 0 && value <= activitiesList.length)
      .map(value => activitiesList[value - 1])
      .join(', ');
    pdfFiledFieldsThird.activitiesOptionsField.setText(selectedActivities);
  }, [pdfFiledFieldsThird.activitiesOptionsField]);
  const setEmployeeCountField = useCallback((value: number) => {
    if (!pdfFiledFieldsFourth.employeeCountField) return;
    pdfFiledFieldsFourth.employeeCountField.setText(value.toString());
  }, [pdfFiledFieldsFourth.employeeCountField]);

  const [formDataFirstPart, setFormDataFirstPart] = useState<FormDataFirstPart>(
    {
      firstName: (firstPart?.firstName ? firstPart.firstName : teachr?.firstName) ?? "",
      lastName: (firstPart?.lastName ? firstPart.lastName : teachr?.lastName) ?? "",
      birthDate: firstPart?.birthDate
        ? typeof firstPart.birthDate === "string"
          ? firstPart.birthDate
          : moment(firstPart.birthDate).format("YYYY-MM-DD")
        : teachr?.birthday
          ? typeof teachr.birthday === "string"
            ? teachr.birthday
            : moment(teachr.birthday).format("YYYY-MM-DD")
          : "",
      birthCity: firstPart?.birthCity ?? "",
      address: firstPart?.address ?? "",
      signCity: firstPart?.signCity ?? "",
      gender: firstPart?.gender
        ? firstPart.gender
        : teachr?.sex === "MAN"
          ? 2
          : teachr?.sex === "WOMAN"
            ? 1
            : 0,
      phone: (firstPart?.phone || teachr?.phone) ?? "",
      country: firstPart?.country || "",
      nationality: firstPart?.nationality || 0,
      maritalStatus: firstPart?.maritalStatus || 0,
      addressObj: firstPart?.addressObj
    },
  );

  const [formDataSecondPart, setFormDataSecondPart] = useState<FormDataSecondPart>({
    hasActivity: secondPart?.hasActivity || 0,
    isAe: secondPart?.isAe || 0,
    socialTaxMode: secondPart?.socialTaxMode || 0,
    // paymentMode: 0,
    socialSecurityNumber: secondPart?.socialSecurityNumber || "",
    healthInsurance: secondPart?.healthInsurance || 0,
    healthInsuranceOther: secondPart?.healthInsuranceOther || "",
    activityName: secondPart?.activityName || "",
  });
  const [formDataThirdPart, setFormDataThirdPart] = useState<FormDataThirdPart>(
    {
      otherActivities: thirdPart?.otherActivities || 0,
      fixAddressAtHome: thirdPart?.fixAddressAtHome || 0,
      useDomiciliationCompany: thirdPart?.useDomiciliationCompany || 0,
      domiciliationAddress: thirdPart?.domiciliationAddress || '',
      activitiesOptions: Array.isArray(thirdPart?.activitiesOptions)
        ? thirdPart.activitiesOptions
        : thirdPart?.activitiesOptions
          ? [thirdPart.activitiesOptions]
          : [],
      optForLiberatoryPayment: thirdPart?.optForLiberatoryPayment || 0,
    },
  );
  const [formDataFourthPart, setFormDataFourthPart] = useState<FormDataFourthPart>({
    renounceInsaisissability: fourthPart?.renounceInsaisissability || 0,
    addCommercialName: fourthPart?.addCommercialName || 0,
    employEmployees: fourthPart?.employEmployees || 0,
    employeeCount: fourthPart?.employeeCount || 0,
    addOtherLegalRepresentatives:
      fourthPart?.addOtherLegalRepresentatives || 0,
  });
  const [formDataFifthPart, setFormDataFifthPart] = useState<FormDataFifthPart>({
    authorizeSirenDirectoryAccess: fifthPart?.authorizeSirenDirectoryAccess || 0,
    authorizePersonalDataProspecting:
      fifthPart?.authorizePersonalDataProspecting || 0,
    addDomainName: fifthPart?.addDomainName || 0,
    domainNameValue: fifthPart?.domainNameValue || "",
  }

  );
  const [legalRepresentatives, setLegalRepresentatives] = useState<LegalRepresentative[]>(Array.isArray(legalRepProc) ? legalRepProc : []);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogFormData, setDialogFormData] = useState<DialogFormData>({
    firstName: "",
    lastName: "",
    birthDate: "",
    birthCountry: "",
    gender: 0,
    nationality: 0,
  });
  const [errorsFirstPart, setErrorsFirstPart] = useState<FormErrorsFirstPart>({
    firstName: "",
    lastName: "",
    birthDate: "",
    birthCity: "",
    address: "",
    signCity: "",
    gender: "",
    phone: "",
    country: "",
    nationality: "",
    maritalStatus: "",
  });

  const [formDataFirstPartSap, setFormDataFirstPartSap] = useState<FormDataFirstPartSap>({
    firstName: (firstPartSap?.firstName ? firstPartSap.firstName : teachr?.firstName) ?? "",
    lastName: (firstPartSap?.lastName ? firstPartSap.lastName : teachr?.lastName) ?? "",
    birthDate: firstPartSap?.birthDate
      ? typeof firstPartSap.birthDate === "string"
        ? firstPartSap.birthDate
        : moment(firstPartSap.birthDate).format("YYYY-MM-DD")
      : teachr?.birthday
        ? typeof teachr.birthday === "string"
          ? teachr.birthday
          : moment(teachr.birthday).format("YYYY-MM-DD")
        : "",
    birthCity: firstPartSap?.birthCity ?? "",
    siret: firstPartSap?.siret ?? "",
    address: firstPartSap?.address ?? "",
    signCity: firstPartSap?.signCity ?? "",
    addressObj: firstPartSap?.addressObj
  });

  const [errorsFirstPartSap, setErrorsFirstPartSap] = useState<FormErrorsFirstPartSap>({
    firstName: "",
    lastName: "",
    birthDate: "",
    birthCity: "",
    siret: "",
    address: "",
    signCity: "",
  });

  const [formDataSecondPartSap, setFormDataSecondPartSap] = useState<FormDataSecondPartSap>({
    apeCode: secondPartSap?.apeCode ?? "",
    createdAt: secondPartSap?.createdAt
      ? typeof secondPartSap.createdAt === "string"
        ? secondPartSap.createdAt
        : moment(secondPartSap.createdAt).format("YYYY-MM-DD")
      : "",
    enterprisePhone: secondPartSap?.enterprisePhone ?? "",
    enterpriseEmail: secondPartSap?.enterpriseEmail ?? "",
    gender: secondPartSap?.gender ?? undefined as any,
    chiefFirstName: secondPartSap?.chiefFirstName ?? "",
    chiefLastName: secondPartSap?.chiefLastName ?? "",
    chiefPhone: secondPartSap?.chiefPhone ?? "",
  });

  const [errorsSecondPartSap, setErrorsSecondPartSap] = useState<FormErrorsSecondPartSap>({
    apeCode: "",
    createdAt: "",
    enterprisePhone: "",
    enterpriseEmail: "",
    gender: "",
    chiefFirstName: "",
    chiefLastName: "",
    chiefPhone: "",
  });

  const [errorsSecondPart, setErrorsSecondPart] = useState<FormErrorsSecondPart>({
    hasActivity: "",
    isAe: "",
    socialTaxMode: "",
    // paymentMode: '',
    socialSecurityNumber: "",
    healthInsurance: "",
    healthInsuranceOther: "",
    activityName: "",
  });
  const [errorsThirdPart, setErrorsThirdPart] = useState<FormErrorsThirdPart>({
    otherActivities: "",
    fixAddressAtHome: "",
    useDomiciliationCompany: "",
    domiciliationAddress: "",
    activitiesOptions: "",
    optForLiberatoryPayment: "",
  });
  const [errorsFourthPart, setErrorsFourthPart] = useState<FormErrorsFourthPart>({
    renounceInsaisissability: "",
    addCommercialName: "",
    employEmployees: "",
    employeeCount: "",
    addOtherLegalRepresentatives: "",
  });
  const [errorsFifthPart, setErrorsFifthPart] = useState<FormErrorsFifthPart>({
    authorizeSirenDirectoryAccess: "",
    authorizePersonalDataProspecting: "",
    addDomainName: "",
    domainNameValue: "",
  });
  const [dialogErrors, setDialogErrors] = useState<DialogErrors>({
    firstName: "",
    lastName: "",
    birthDate: "",
    birthCountry: "",
    gender: "",
    nationality: "",
  });

  const handleChange = useCallback(<
    TFormData extends
    | FormDataFirstPart
    | FormDataFirstPartSap
    | FormDataSecondPart
    | FormDataSecondPartSap
    | FormDataThirdPart
    | FormDataFourthPart
    | FormDataFifthPart
    | DialogFormData,

    TFormErrors extends
    | FormErrorsFirstPart
    | FormErrorsFirstPartSap
    | FormErrorsSecondPart
    | FormErrorsSecondPartSap
    | FormErrorsThirdPart
    | FormErrorsFourthPart
    | FormErrorsFifthPart
    | DialogErrors,
  >(
    data: changeInputData<TFormData>,
    e: React.ChangeEvent<HTMLInputElement>,
    values: changeInputInterface<TFormData, TFormErrors>,
    page?: number,
    blocked?: boolean,
    valueSap?: string
  ) => {
    values.setField(
      (prev) => ({ ...prev, [data.field]: e.target.value }) as TFormData,
    );
    const errorKey = data.field as unknown as keyof TFormErrors;
    if (values.error[errorKey]) {
      values.setError((prev) => ({ ...prev, [errorKey]: "" }) as TFormErrors);
    }
    if (data.pdfField) {
      if (valueSap) {
        data.pdfField.setText(valueSap);
      } else {
        data.pdfField.setText(e.target.value);
      }
    }
    if (data.pdfField2) {
      data.pdfField2.setText(e.target.value);
    }
    if (setPage && page) {
      setPage(page)
    }
    if (triggerPdfUpdate && !blocked) {
      triggerPdfUpdate();
    }
  }, [setPage, triggerPdfUpdate]);

  const handleSelect = useCallback(<
    TFormData extends
    | FormDataFirstPart
    | FormDataFirstPartSap
    | FormDataSecondPart
    | FormDataSecondPartSap
    | FormDataThirdPart
    | FormDataFourthPart
    | FormDataFifthPart
    | DialogFormData,
    TFormErrors extends
    | FormErrorsFirstPart
    | FormErrorsFirstPartSap
    | FormErrorsSecondPart
    | FormErrorsSecondPartSap
    | FormErrorsThirdPart
    | FormErrorsFourthPart
    | FormErrorsFifthPart
    | DialogErrors,
  >(
    value: number,
    field: keyof TFormData,
    setPdfRadioGroup: (value: number) => void,
    values: changeInputInterface<TFormData, TFormErrors>,
    page?: number,
    blocked?: boolean
  ) => {
    values.setField((prev) => ({ ...prev, [field]: value }) as TFormData);
    setPdfRadioGroup(value);
    const errorKey = field as unknown as keyof TFormErrors;
    if (values.error[errorKey]) {
      values.setError((prev) => ({ ...prev, [errorKey]: "" }));
    }
    // Déclencher la mise à jour du PDF
    if (setPage && page) {
      setPage(page)
    }
    if (triggerPdfUpdate && !blocked) {
      triggerPdfUpdate();
    }
  }, [setPage, triggerPdfUpdate]);

  const prevValuesRef = useRef<{
    formDataFirstPart?: FormDataFirstPart;
    formDataSecondPart?: FormDataSecondPart;
    formDataThirdPart?: FormDataThirdPart;
    formDataFourthPart?: FormDataFourthPart;
    formDataFifthPart?: FormDataFifthPart;
  }>({});

  useEffect(() => {
    // Vérifier que pdfDoc est disponible et que c'est une procuration AE
    if (!pdfDoc || !isAeProcuration) return;

    // Vérifier si les valeurs ont réellement changé pour éviter les mises à jour inutiles
    const hasChanged =
      JSON.stringify(prevValuesRef.current.formDataFirstPart) !== JSON.stringify(formDataFirstPart) ||
      JSON.stringify(prevValuesRef.current.formDataSecondPart) !== JSON.stringify(formDataSecondPart) ||
      JSON.stringify(prevValuesRef.current.formDataThirdPart) !== JSON.stringify(formDataThirdPart) ||
      JSON.stringify(prevValuesRef.current.formDataFourthPart) !== JSON.stringify(formDataFourthPart) ||
      JSON.stringify(prevValuesRef.current.formDataFifthPart) !== JSON.stringify(formDataFifthPart);

    if (!hasChanged && prevValuesRef.current.formDataFirstPart) {
      return; // Pas de changement, ne pas mettre à jour
    }

    // Mettre à jour les valeurs précédentes
    prevValuesRef.current = {
      formDataFirstPart: { ...formDataFirstPart },
      formDataSecondPart: { ...formDataSecondPart },
      formDataThirdPart: { ...formDataThirdPart },
      formDataFourthPart: { ...formDataFourthPart },
      formDataFifthPart: { ...formDataFifthPart },
    };

    try {
      // FormDataFirstPart - Champs texte
      if (pdfFiledFieldsFirst.firstNameField1) {
        pdfFiledFieldsFirst.firstNameField1.setText(formDataFirstPart.firstName || '');
      }
      if (pdfFiledFieldsFirst.firstNameField2) {
        pdfFiledFieldsFirst.firstNameField2.setText(formDataFirstPart.firstName || '');
      }
      if (pdfFiledFieldsFirst.lastNameField1) {
        pdfFiledFieldsFirst.lastNameField1.setText(formDataFirstPart.lastName || '');
      }
      if (pdfFiledFieldsFirst.lastNameField2) {
        pdfFiledFieldsFirst.lastNameField2.setText(formDataFirstPart.lastName || '');
      }

      // Date de naissance
      if (formDataFirstPart.birthDate) {
        try {
          const pdfDate = moment(formDataFirstPart.birthDate).format('DD/MM/YYYY');
          if (pdfFiledFieldsFirst.birthDateField1) {
            pdfFiledFieldsFirst.birthDateField1.setText(pdfDate);
          }
          if (pdfFiledFieldsFirst.birthDateField2) {
            pdfFiledFieldsFirst.birthDateField2.setText(pdfDate);
          }
        } catch (error) {
          console.warn('Erreur lors du formatage de la date de naissance:', error);
        }
      }

      // Autres champs texte
      if (pdfFiledFieldsFirst.birthCityField1) {
        pdfFiledFieldsFirst.birthCityField1.setText(formDataFirstPart.birthCity || '');
      }
      if (pdfFiledFieldsFirst.birthCityField2) {
        pdfFiledFieldsFirst.birthCityField2.setText(formDataFirstPart.birthCity || '');
      }
      if (pdfFiledFieldsFirst.addressField1) {
        pdfFiledFieldsFirst.addressField1.setText(formDataFirstPart.address || '');
      }
      // addressField2 est utilisé pour l'adresse de domiciliation (formDataThirdPart)
      // Ne pas le mettre à jour ici, il sera mis à jour dans la section FormDataThirdPart
      if (pdfFiledFieldsFirst.signCityField) {
        pdfFiledFieldsFirst.signCityField.setText(formDataFirstPart.signCity || '');
      }
      if (pdfFiledFieldsFirst.phoneField) {
        pdfFiledFieldsFirst.phoneField.setText(formDataFirstPart.phone || '');
      }
      if (pdfFiledFieldsFirst.countryField) {
        pdfFiledFieldsFirst.countryField.setText(formDataFirstPart.country || '');
      }
      // if (pdfFiledFieldsFirst.nationalityField) {
      //   pdfFiledFieldsFirst.nationalityField.setText(formDataFirstPart.nationality || '');
      // }

      // Champs radio/dropdown
      if (formDataFirstPart.nationality !== 0 && formDataFirstPart.nationality !== undefined && formDataFirstPart.nationality !== null) {
        setNationality(formDataFirstPart.nationality);
      }
      if (formDataFirstPart.gender !== 0 && formDataFirstPart.gender !== undefined && formDataFirstPart.gender !== null) {
        setGenderField(formDataFirstPart.gender);
      }
      if (formDataFirstPart.maritalStatus !== 0 && formDataFirstPart.maritalStatus !== undefined && formDataFirstPart.maritalStatus !== null) {
        setMaritalStatusField(formDataFirstPart.maritalStatus);
      }

      // FormDataSecondPart
      if (pdfFiledFieldsSecond.socialSecurityNumberField) {
        pdfFiledFieldsSecond.socialSecurityNumberField.setText(formDataSecondPart.socialSecurityNumber || '');
      }
      if (formDataSecondPart.healthInsurance !== 0 && formDataSecondPart.healthInsurance !== undefined && formDataSecondPart.healthInsurance !== null) {
        setHealthInsuranceField(formDataSecondPart.healthInsurance);
      }
      if (formDataSecondPart.healthInsurance === 6 && pdfFiledFieldsSecond.healthInsuranceOtherField) {
        pdfFiledFieldsSecond.healthInsuranceOtherField.setText(formDataSecondPart.healthInsuranceOther || '');
      }
      if (formDataSecondPart.socialTaxMode !== 0 && formDataSecondPart.socialTaxMode !== undefined && formDataSecondPart.socialTaxMode !== null) {
        setSocialTaxField(formDataSecondPart.socialTaxMode);
      }
      if (formDataSecondPart.hasActivity !== 0 && formDataSecondPart.hasActivity !== undefined && formDataSecondPart.hasActivity !== null) {
        setIsActivityField(formDataSecondPart.hasActivity);
      }
      if (formDataSecondPart.hasActivity === 1 && pdfFiledFieldsSecond.activityNameField) {
        pdfFiledFieldsSecond.activityNameField.setText(formDataSecondPart.activityName || '');
      }
      if (formDataSecondPart.isAe !== 0 && formDataSecondPart.isAe !== undefined && formDataSecondPart.isAe !== null) {
        setIsAeField(formDataSecondPart.isAe);
      }

      // FormDataThirdPart
      if (formDataThirdPart.otherActivities !== 0 && formDataThirdPart.otherActivities !== undefined && formDataThirdPart.otherActivities !== null) {
        setOtherActivityField(formDataThirdPart.otherActivities);
      }
      if (formDataThirdPart.otherActivities === 1 && Array.isArray(formDataThirdPart.activitiesOptions) && formDataThirdPart.activitiesOptions.length > 0) {
        setActivitiesOptionsField(formDataThirdPart.activitiesOptions);
      }
      if (formDataThirdPart.fixAddressAtHome !== 0 && formDataThirdPart.fixAddressAtHome !== undefined && formDataThirdPart.fixAddressAtHome !== null) {
        setFixAddressAtHomeField(formDataThirdPart.fixAddressAtHome);
      }
      if (formDataThirdPart.useDomiciliationCompany !== 0 && formDataThirdPart.useDomiciliationCompany !== undefined && formDataThirdPart.useDomiciliationCompany !== null) {
        setUseDomiciliationCompanyField(formDataThirdPart.useDomiciliationCompany);
      }
      if (formDataThirdPart.optForLiberatoryPayment !== 0 && formDataThirdPart.optForLiberatoryPayment !== undefined && formDataThirdPart.optForLiberatoryPayment !== null) {
        setOptForLiberatoryPaymentField(formDataThirdPart.optForLiberatoryPayment);
      }

      // Adresse de domiciliation (utilise addressField2)
      // L'adresse de domiciliation n'est utilisée que si useDomiciliationCompany === 1
      if (formDataThirdPart.useDomiciliationCompany === 1 && formDataThirdPart.domiciliationAddress && pdfFiledFieldsFirst.addressField2) {
        pdfFiledFieldsFirst.addressField2.setText(formDataThirdPart.domiciliationAddress);
      } else if (formDataThirdPart.fixAddressAtHome === 1 && pdfFiledFieldsFirst.addressField2) {
        // Si l'adresse est fixée au domicile, on vide le champ addressField2
        pdfFiledFieldsFirst.addressField2.setText('');
      }

      // FormDataFourthPart
      if (formDataFourthPart.renounceInsaisissability !== 0 && formDataFourthPart.renounceInsaisissability !== undefined && formDataFourthPart.renounceInsaisissability !== null) {
        setRenounceInsaisissabilityField(formDataFourthPart.renounceInsaisissability);
      }
      if (formDataFourthPart.addCommercialName !== 0 && formDataFourthPart.addCommercialName !== undefined && formDataFourthPart.addCommercialName !== null) {
        setAddCommercialNameField(formDataFourthPart.addCommercialName);
      }
      if (formDataFourthPart.employEmployees !== 0 && formDataFourthPart.employEmployees !== undefined && formDataFourthPart.employEmployees !== null) {
        setEmployEmployeesField(formDataFourthPart.employEmployees);
      }
      if (formDataFourthPart.employEmployees === 1 && formDataFourthPart.employeeCount !== 0 && formDataFourthPart.employeeCount !== undefined && formDataFourthPart.employeeCount !== null) {
        setEmployeeCountField(formDataFourthPart.employeeCount);
      }
      if (formDataFourthPart.addOtherLegalRepresentatives !== 0 && formDataFourthPart.addOtherLegalRepresentatives !== undefined && formDataFourthPart.addOtherLegalRepresentatives !== null) {
        setAddOtherLegalRepresentativesField(formDataFourthPart.addOtherLegalRepresentatives);
      }

      // FormDataFifthPart
      if (formDataFifthPart.authorizeSirenDirectoryAccess !== 0 && formDataFifthPart.authorizeSirenDirectoryAccess !== undefined && formDataFifthPart.authorizeSirenDirectoryAccess !== null) {
        setAuthorizeSirenDirectoryAccessField(formDataFifthPart.authorizeSirenDirectoryAccess);
      }
      if (formDataFifthPart.authorizePersonalDataProspecting !== 0 && formDataFifthPart.authorizePersonalDataProspecting !== undefined && formDataFifthPart.authorizePersonalDataProspecting !== null) {
        setAuthorizePersonalDataProspectingField(formDataFifthPart.authorizePersonalDataProspecting);
      }
      if (formDataFifthPart.addDomainName !== 0 && formDataFifthPart.addDomainName !== undefined && formDataFifthPart.addDomainName !== null) {
        setAddDomainNameField(formDataFifthPart.addDomainName);
      }
      if (formDataFifthPart.addDomainName === 1 && formDataFifthPart.domainNameValue && pdfFiledFieldsFifth.domainNamesecondSaveField) {
        pdfFiledFieldsFifth.domainNamesecondSaveField.setText(formDataFifthPart.domainNameValue || '');
      }

    } catch (error) {
      console.error('Erreur lors de la mise à jour des champs PDF:', error);
    }

    if (triggerPdfUpdate) triggerPdfUpdate();
  }, [pdfDoc, isAeProcuration, formDataFirstPart.firstName, formDataFirstPart.lastName, formDataFirstPart.birthDate, formDataFirstPart.birthCity, formDataFirstPart.address, formDataFirstPart.signCity, formDataFirstPart.phone, formDataFirstPart.country, formDataFirstPart.nationality, formDataFirstPart.gender, formDataFirstPart.maritalStatus, formDataSecondPart.socialSecurityNumber, formDataSecondPart.healthInsurance, formDataSecondPart.healthInsuranceOther, formDataSecondPart.socialTaxMode, formDataSecondPart.hasActivity, formDataSecondPart.activityName, formDataSecondPart.isAe, formDataThirdPart.otherActivities, formDataThirdPart.activitiesOptions, formDataThirdPart.fixAddressAtHome, formDataThirdPart.useDomiciliationCompany, formDataThirdPart.domiciliationAddress, formDataThirdPart.optForLiberatoryPayment, formDataFourthPart.renounceInsaisissability, formDataFourthPart.addCommercialName, formDataFourthPart.employEmployees, formDataFourthPart.employeeCount, formDataFourthPart.addOtherLegalRepresentatives, formDataFifthPart.authorizeSirenDirectoryAccess, formDataFifthPart.authorizePersonalDataProspecting, formDataFifthPart.addDomainName, formDataFifthPart.domainNameValue, pdfFiledFieldsFirst, pdfFiledFieldsSecond, pdfFiledFieldsThird, pdfFiledFieldsFourth, pdfFiledFieldsFifth, formDataFirstPart, formDataSecondPart, formDataThirdPart, formDataFourthPart, formDataFifthPart, setGenderField, setMaritalStatusField, setHealthInsuranceField, setSocialTaxField, setIsActivityField, setIsAeField, setOtherActivityField, setActivitiesOptionsField, setFixAddressAtHomeField, setUseDomiciliationCompanyField, setOptForLiberatoryPaymentField, setRenounceInsaisissabilityField, setAddCommercialNameField, setEmployEmployeesField, setEmployeeCountField, setAddOtherLegalRepresentativesField, setAuthorizeSirenDirectoryAccessField, setAuthorizePersonalDataProspectingField, setAddDomainNameField, setNationality, triggerPdfUpdate])

  const prevSapValuesRef = useRef<FormDataFirstPartSap | null>(null);

  // useEffect pour mettre à jour les champs PDF SAP
  useMemo(() => {
    // Vérifier que pdfDoc est disponible et que c'est une procuration SAP
    if (!pdfDoc || !isSapProcuration) return;

    // Vérifier si les valeurs ont réellement changé
    // const hasChanged = JSON.stringify(prevSapValuesRef.current) !== JSON.stringify(formDataFirstPartSap);

    // if (!hasChanged && prevSapValuesRef.current) {
    //   return; // Pas de changement, ne pas mettre à jour
    // }

    // Mettre à jour la valeur précédente
    prevSapValuesRef.current = { ...formDataFirstPartSap };

    try {
      // Nom complet (prénom + nom)
      if (pdfFiledFieldsFirstSap.fullNameField) {
        const fullName = `${formDataFirstPartSap.firstName || ''} ${formDataFirstPartSap.lastName || ''}`.trim();
        pdfFiledFieldsFirstSap.fullNameField.setText(fullName);
      }

      // Date de naissance
      if (formDataFirstPartSap.birthDate) {
        try {
          const pdfDate = moment(formDataFirstPartSap.birthDate).format('DD/MM/YYYY');
          if (pdfFiledFieldsFirstSap.birthDateField) {
            pdfFiledFieldsFirstSap.birthDateField.setText(pdfDate);
          }
        } catch (error) {
          console.warn('Erreur lors du formatage de la date de naissance SAP:', error);
        }
      }

      // Ville de naissance
      if (pdfFiledFieldsFirstSap.cityBirthField) {
        pdfFiledFieldsFirstSap.cityBirthField.setText(formDataFirstPartSap.birthCity || '');
      }

      // SIRET
      if (pdfFiledFieldsFirstSap.siretField) {
        pdfFiledFieldsFirstSap.siretField.setText(formDataFirstPartSap.siret || '');
      }

      // Adresse
      if (pdfFiledFieldsFirstSap.addressField) {
        pdfFiledFieldsFirstSap.addressField.setText(formDataFirstPartSap.address || '');
      }

      // Ville de signature
      if (pdfFiledFieldsFirstSap.signCityField) {
        pdfFiledFieldsFirstSap.signCityField.setText(formDataFirstPartSap.signCity || '');
      }

    } catch (error) {
      console.error('Erreur lors de la mise à jour des champs PDF SAP:', error);
    }
  }, [pdfDoc, isSapProcuration, pdfFiledFieldsFirstSap, formDataFirstPartSap])

  const setGenderFieldSap = useCallback((value: number) => {
    if (!pdfFiledFieldsSecondSap?.genderField) return;
    switch (value) {
      case 0:
        pdfFiledFieldsSecondSap.genderField.select("Value_ymsg"); // Madame
        break;
      case 1:
        pdfFiledFieldsSecondSap.genderField.select("Value_njst"); // Monsieur
        break;
      default:
        pdfFiledFieldsSecondSap.genderField.select("Value_ymsg"); // Madame par défaut
        break;
    }
  }, [pdfFiledFieldsSecondSap?.genderField]);

  // useMemo pour mettre à jour les champs PDF SAP second part
  useMemo(() => {
    if (!pdfDoc || !isSapProcuration) return;

    try {
      // Code APE
      if (pdfFiledFieldsSecondSap.codeApeField) {
        pdfFiledFieldsSecondSap.codeApeField.setText(formDataSecondPartSap.apeCode || '');
      }

      // Date de création
      if (formDataSecondPartSap.createdAt) {
        try {
          const pdfDate = moment(formDataSecondPartSap.createdAt).format('DD/MM/YYYY');
          if (pdfFiledFieldsSecondSap.createdAtField) {
            pdfFiledFieldsSecondSap.createdAtField.setText(pdfDate);
          }
        } catch (error) {
          console.warn('Erreur lors du formatage de la date de création SAP:', error);
        }
      }

      // Téléphone entreprise
      if (pdfFiledFieldsSecondSap.enterprisePhoneField) {
        pdfFiledFieldsSecondSap.enterprisePhoneField.setText(formDataSecondPartSap.enterprisePhone || '');
      }

      // Email entreprise
      if (pdfFiledFieldsSecondSap.enterpriseEmailField) {
        pdfFiledFieldsSecondSap.enterpriseEmailField.setText(formDataSecondPartSap.enterpriseEmail || '');
      }

      // Prénom dirigeant
      if (pdfFiledFieldsSecondSap.chiefFirstNameField) {
        pdfFiledFieldsSecondSap.chiefFirstNameField.setText(formDataSecondPartSap.chiefFirstName || '');
      }

      // Nom dirigeant
      if (pdfFiledFieldsSecondSap.chiefLastNameField) {
        pdfFiledFieldsSecondSap.chiefLastNameField.setText(formDataSecondPartSap.chiefLastName || '');
      }

      // Téléphone dirigeant
      if (pdfFiledFieldsSecondSap.chiefPhoneField) {
        pdfFiledFieldsSecondSap.chiefPhoneField.setText(formDataSecondPartSap.chiefPhone || '');
      }

      // Genre
      if (formDataSecondPartSap.gender !== undefined && formDataSecondPartSap.gender !== null) {
        setGenderFieldSap(formDataSecondPartSap.gender);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des champs PDF SAP second part:', error);
    }
  }, [pdfDoc, isSapProcuration, pdfFiledFieldsSecondSap, formDataSecondPartSap, setGenderFieldSap]);

  return {
    formDataFirstPart,
    formDataSecondPart,
    formDataThirdPart,
    formDataFourthPart,
    formDataFifthPart,
    formDataFirstPartSap,
    formDataSecondPartSap,
    errorsFirstPart,
    errorsSecondPart,
    errorsThirdPart,
    errorsFourthPart,
    errorsFifthPart,
    errorsFirstPartSap,
    errorsSecondPartSap,
    pdfFiledFieldsFirst,
    pdfFiledFieldsSecond,
    pdfFiledFieldsThird,
    pdfFiledFieldsFourth,
    pdfFiledFieldsFifth,
    pdfFiledFieldsFirstSap,
    pdfFiledFieldsSecondSap,
    legalRepresentatives,
    openDialog,
    dialogFormData,
    dialogErrors,
    listeCountries,
    listeNationalities,
    setFormDataFirstPart,
    setFormDataSecondPart,
    setFormDataThirdPart,
    setFormDataFourthPart,
    setFormDataFifthPart,
    setFormDataFirstPartSap,
    setFormDataSecondPartSap,
    setErrorsFirstPart,
    setErrorsSecondPart,
    setErrorsThirdPart,
    setErrorsFourthPart,
    setErrorsFifthPart,
    setErrorsFirstPartSap,
    setErrorsSecondPartSap,
    setGenderField,
    setMaritalStatusField,
    setNationality,
    setCountryField,
    setHealthInsuranceField,
    setSocialTaxField,
    setIsActivityField,
    setIsAeField,
    setOtherActivityField,
    setFixAddressAtHomeField,
    setUseDomiciliationCompanyField,
    setAddOtherLegalRepresentativesField,
    setRenounceInsaisissabilityField,
    setAddCommercialNameField,
    setEmployEmployeesField,
    setOptForLiberatoryPaymentField,
    setAuthorizeSirenDirectoryAccessField,
    setAuthorizePersonalDataProspectingField,
    setAddDomainNameField,
    handleChange,
    handleSelect,
    setActivitiesOptionsField,
    setEmployeeCountField,
    setLegalRepresentatives,
    setOpenDialog,
    setDialogFormData,
    setDialogErrors,
    setOtherChiefGenderField,
    setOtherChiefNationalField,
    setGenderFieldSap
  };
};
